// Progress metrics. Every number is computed from what the user logged in this app.
// No predictions, no rankings against other people, no streak pressure (knowledge rules
// `no_weight_predictions`, `no_calorie_targets`).

import type { CookedEntry, Feedback, WeightEntry } from '../state/schema';
import type { Plan } from './planner';
import { mainProtein } from './ranking';
import { getRecipe } from './recipes';
import { plateFeatures } from './nutrition/plate';
import type { Goal } from './preferences';
import { addDays, parseISODate, toISODate } from '../lib/dates';
import { plural } from './text';

export interface WeekBar { weekStart: string; label: string; cooked: number }

export interface Progress {
  cookedThisWeek: number;
  cookedLast28: number;
  /** Cooked per week for the last 6 weeks, oldest first (Monday-start weeks). */
  weeks: WeekBar[];
  /** Days in the last 7 with at least one cooked meal. */
  activeDays7: number;
  plan: { due: number; cooked: number; skipped: number } | null;
  distinctRecipes28: number;
  distinctProteins28: number;
  /** Cooked meals in the last 28 days that were balanced plates (protein + vegetable + grain). */
  balanced28: number;
  /** Distinct vegetables across meals cooked in the last 28 days. */
  vegetables28: number;
  /** Use-soon ingredients the user later marked used up (not wasted). */
  usedUp28: number;
  wasted28: number;
  weight: { first: WeightEntry; last: WeightEntry; entries: WeightEntry[] } | null;
}

export function mondayOf(iso: string): string {
  const d = parseISODate(iso);
  const day = (d.getDay() + 6) % 7;
  return addDays(iso, -day);
}

export function computeProgress(input: {
  cooked: CookedEntry[];
  feedback: Feedback[];
  plan: Plan | null;
  planHistory: Plan[];
  weights: WeightEntry[];
  today: string;
}): Progress {
  const { cooked, feedback, plan, planHistory, weights, today } = input;
  const day = (at: string) => toISODate(new Date(at));
  const since28 = addDays(today, -27);
  const since7 = addDays(today, -6);
  const thisMonday = mondayOf(today);
  const recent = cooked.filter((c) => day(c.at) >= since28);

  const weeks: WeekBar[] = [];
  for (let w = 5; w >= 0; w--) {
    const start = addDays(thisMonday, -7 * w);
    const end = addDays(start, 7);
    weeks.push({
      weekStart: start,
      label: w === 0 ? 'Now' : parseISODate(start).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      cooked: cooked.filter((c) => day(c.at) >= start && day(c.at) < end).length,
    });
  }

  const plans = [plan, ...planHistory].filter((p): p is Plan => !!p);
  const due = plans.flatMap((p) => p.entries).filter((e) => e.date >= since28 && e.date < today || (e.date === today && e.status !== 'planned'));
  const planStats = due.length ? { due: due.length, cooked: due.filter((e) => e.status === 'cooked').length, skipped: due.filter((e) => e.status === 'skipped').length } : null;

  const recipes = recent.map((c) => getRecipe(c.recipeId)).filter((r): r is NonNullable<typeof r> => !!r);
  const proteins = new Set(recipes.map(mainProtein).filter(Boolean));
  const veg = new Set(recipes.flatMap((r) => plateFeatures(r).vegetables));
  const recentFb = feedback.filter((f) => 'at' in f && day(f.at) >= since28);

  const sorted = [...weights].sort((a, b) => a.date.localeCompare(b.date));
  return {
    cookedThisWeek: cooked.filter((c) => day(c.at) >= thisMonday).length,
    cookedLast28: recent.length,
    weeks,
    activeDays7: new Set(cooked.filter((c) => day(c.at) >= since7).map((c) => day(c.at))).size,
    plan: planStats,
    distinctRecipes28: new Set(recent.map((c) => c.recipeId)).size,
    distinctProteins28: proteins.size,
    balanced28: recipes.filter((r) => r.meal === 'main' && plateFeatures(r).balanced).length,
    vegetables28: veg.size,
    usedUp28: recentFb.filter((f) => f.type === 'used_up').length,
    wasted28: recentFb.filter((f) => f.type === 'wasted').length,
    weight: sorted.length ? { first: sorted[0], last: sorted[sorted.length - 1], entries: sorted } : null,
  };
}

/**
 * One or two sentences that interpret progress for the user's goals. Factual, encouraging,
 * never a promise or prediction. Returns nothing when there's too little data to say anything.
 */
export function goalSummary(p: Progress, goals: Goal[]): string[] {
  const out: string[] = [];
  if (p.cookedLast28 === 0) return out;
  const g = new Set(goals);
  if (p.plan && p.plan.due >= 3) out.push(`You cooked ${p.plan.cooked} of ${p.plan.due} planned dinners in the last four weeks.`);
  if (g.has('balanced') || g.has('maintain') || g.has('lose')) {
    if (p.balanced28) out.push(`${plural(p.balanced28, 'dinner')} in the last four weeks had protein, vegetables and a grain together.`);
  }
  if (g.has('quality') && p.vegetables28) out.push(`You cooked with ${plural(p.vegetables28, 'different vegetable')} in the last four weeks.`);
  if ((g.has('muscle') || g.has('performance') || g.has('gain')) && p.distinctProteins28) {
    out.push(`Your dinners drew on ${plural(p.distinctProteins28, 'protein source')} in the last four weeks.`);
  }
  if (!out.length) out.push(`You cooked ${plural(p.distinctRecipes28, 'different dish', 'different dishes')} in the last four weeks.`);
  return out.slice(0, 2);
}

/** Change in kg between first and last logged weight. Reported as logged history, never projected. */
export function weightChange(p: Progress): number | null {
  if (!p.weight || p.weight.entries.length < 2) return null;
  return Math.round((p.weight.last.kg - p.weight.first.kg) * 10) / 10;
}

export function kgToLb(kg: number): number {
  return Math.round(kg * 2.20462 * 10) / 10;
}
export function lbToKg(lb: number): number {
  return Math.round((lb / 2.20462) * 10) / 10;
}
